import { lowerCamelCase, present, upperCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { flooringAreaValueNamePresenter } from '@kaiizen/kai-presenters';
import {
  getAllFlooringAreaValueNames,
  isReservedFlooringAreaValueName,
  normalizeFlooringAreaValueName,
  ScanTemplateGroup,
  ScanTemplateRoot,
  ScanTemplateValue,
  STANDARD_ROOM_GROUP_VALUES,
  updateTemplateGroup,
} from '@kaiizen/kai-scan-template-system';
import { Fragment, useMemo, useState } from 'react';
import { ScanTemplateDataMutators } from '../../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button, Checkbox, LabeledInput } from '../../Widgets';
import { MAX_VALUE_NAME_LENGTH } from './scanTemplateFieldLimits';

const GROUP_MEASUREMENTS_EDITOR_STYLE = css`
  label: GROUP_MEASUREMENTS_EDITOR_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const ADD_FLOORING_TYPE_BAR_STYLE = css`
  label: ADD_FLOORING_TYPE_BAR_STYLE;
  display: flex;
  align-items: end;
  gap: ${LAYOUT2.spacing1}px;

  > *:first-of-type {
    max-width: 320px;
  }
`;

const TABLE_CONTAINER_STYLE = css`
  label: TABLE_CONTAINER_STYLE;
  overflow-x: auto;
`;

const GROUP_NAME_COLUMN_WIDTH = 250;
const FLOORING_TYPE_COLUMN_WIDTH = 150;

const TABLE_STYLE = css`
  label: TABLE_STYLE;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th, td {
    width: ${FLOORING_TYPE_COLUMN_WIDTH}px;
    min-width: ${FLOORING_TYPE_COLUMN_WIDTH}px;
    max-width: ${FLOORING_TYPE_COLUMN_WIDTH}px;
    padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
    border-bottom: 1px solid var(--border2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  td input[type="checkbox"] {
    margin: 0 auto;
  }

  thead th {
    ${TYPOGRAPHY2.smallBold}
    color: var(--text);
    background: var(--background);
    border-bottom: 1px solid var(--border1);
    white-space: normal; /* long column titles wrap instead of truncating */
    vertical-align: bottom;
  }

  th:first-of-type, td:first-of-type {
    width: ${GROUP_NAME_COLUMN_WIDTH}px;
    min-width: ${GROUP_NAME_COLUMN_WIDTH}px;
    max-width: ${GROUP_NAME_COLUMN_WIDTH}px;
    position: sticky;
    left: 0;
    background: var(--background);
    z-index: 1;
    text-align: left;
  }
`;

const GROUP_NAME_CELL_STYLE = css`
  label: GROUP_NAME_CELL_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text);
`;

const GROUP_NAME_LINK_STYLE = css`
  label: GROUP_NAME_LINK_STYLE;
  color: var(--textGreen);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const CATEGORY_DIVIDER_CELL_STYLE = css`
  label: CATEGORY_DIVIDER_CELL_STYLE;
  background: var(--background2) !important;
`;

const CATEGORY_DIVIDER_LABEL_STYLE = css`
  label: CATEGORY_DIVIDER_LABEL_STYLE;
  ${TYPOGRAPHY2.smallBold}
  color: var(--text2);
  text-transform: uppercase;
  position: sticky;
  left: ${LAYOUT2.spacing2}px;
  display: inline-block;
`;

const STANDARD_MEASUREMENTS_FOOTNOTE_STYLE = css`
  label: STANDARD_MEASUREMENTS_FOOTNOTE_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

const DEFAULT_CATEGORY_NAME = 'other';

const getFlooringValueUpdate = (checked: boolean): ScanTemplateValue | null => checked ? { type: 'sqft', meaningType: 'flooringAreaMeasurement' } : null;

const groupHasFlooringType = (group: ScanTemplateGroup, flooringType: string): boolean =>
  group.values?.[flooringType]?.meaningType === 'flooringAreaMeasurement';

const ROOM_VALUE_KEYS = Object.keys(STANDARD_ROOM_GROUP_VALUES);

// derived from the standard template validator's STANDARD_ROOM_GROUP_VALUES so the footnote can't drift
const STANDARD_MEASUREMENT_INPUT_NAMES = Object.entries(STANDARD_ROOM_GROUP_VALUES)
  .filter(([, value]) => (value as ScanTemplateValue).value === undefined)
  .map(([name]) => name);
const STANDARD_MEASUREMENT_CALCULATIONS = Object.entries(STANDARD_ROOM_GROUP_VALUES)
  .filter(([, value]) => (value as ScanTemplateValue).value !== undefined)
  .map(([name, value]) => `${name} = ${(value as ScanTemplateValue).value}`);

// same semantics as the "Use standard Room Group measurements" toggle in the group editor side panel
const groupHasRoomValues = (group: ScanTemplateGroup): boolean => ROOM_VALUE_KEYS.some(key => key in (group.values ?? {}));

const getRoomValuesUpdate = (checked: boolean): Record<string, ScanTemplateValue | null> =>
  checked ? STANDARD_ROOM_GROUP_VALUES : Object.fromEntries(ROOM_VALUE_KEYS.map(key => [key, null]));

// Same ordering as the template editor's left navigator: categories sorted
// alphabetically (interior/exterior/other always present there), groups in template order within each category
const getGroupsClusteredByCategory = (groups: ScanTemplateGroup[]): { category: string; groups: ScanTemplateGroup[] }[] => {
  const categorizedGroups: Record<string, ScanTemplateGroup[]> = {};
  groups.forEach(group => {
    const category = lowerCamelCase(group.category || DEFAULT_CATEGORY_NAME);
    categorizedGroups[category] = [...(categorizedGroups[category] || []), group];
  });
  return Object.keys(categorizedGroups).sort().map(category => ({ category, groups: categorizedGroups[category] }));
};

export const ScanTemplateGroupMeasurementsEditor = ({
  templateRoot,
  scanTemplateDataMutators,
  onGroupClick,
  readOnly,
}: {
  templateRoot: ScanTemplateRoot;
  scanTemplateDataMutators: ScanTemplateDataMutators;
  onGroupClick?: (groupId: string) => void;
  readOnly?: boolean;
}) => {
  // flooring types added here but not yet checked on any group; they live only in this component's state
  const [draftFlooringTypes, setDraftFlooringTypes] = useState<string[]>([]);
  const [newFlooringTypeName, setNewFlooringTypeName] = useState('');

  const existingFlooringTypes = useMemo(() => getAllFlooringAreaValueNames(templateRoot), [templateRoot]);
  const flooringTypes = useMemo(
    () => [...new Set([...existingFlooringTypes, ...draftFlooringTypes])].sort((a, b) => a.localeCompare(b)),
    [existingFlooringTypes, draftFlooringTypes],
  );

  const clusteredGroups = useMemo(
    () => getGroupsClusteredByCategory(templateRoot.groupTemplates ?? []),
    [templateRoot.groupTemplates],
  );

  const handleAddFlooringType = () => {
    const name = normalizeFlooringAreaValueName(newFlooringTypeName);
    if (!present(name)) return;
    if (isReservedFlooringAreaValueName(name)) {
      alert(`"${name}" is a standard room value and can't be used as a flooring type`);
      return;
    }
    if (flooringTypes.includes(name)) {
      alert(`Flooring type "${flooringAreaValueNamePresenter(name)}" already exists`);
      return;
    }
    setDraftFlooringTypes([...draftFlooringTypes, name]);
    setNewFlooringTypeName('');
  };

  const handleToggleCell = (group: ScanTemplateGroup, flooringType: string, checked: boolean) => {
    scanTemplateDataMutators.updatePart({
      groupId: group.id,
      updates: { values: { [flooringType]: getFlooringValueUpdate(checked) } },
    });
  };

  const handleToggleCategory = (categoryGroups: ScanTemplateGroup[], flooringType: string, checked: boolean) => {
    // chain pure-functional group updates in memory, then apply them all with a single
    // state update (which also triggers a single back-end save)
    const updatedTemplateRoot = categoryGroups.reduce((template, group) => {
      if (groupHasFlooringType(group, flooringType) === checked) return template;
      return updateTemplateGroup(template, group.id, { values: { [flooringType]: getFlooringValueUpdate(checked) } });
    }, templateRoot);
    if (updatedTemplateRoot === templateRoot) return;
    scanTemplateDataMutators.updateTemplate({ groupTemplates: updatedTemplateRoot.groupTemplates });
  };

  const handleToggleRoomValuesCell = (group: ScanTemplateGroup, checked: boolean) => {
    scanTemplateDataMutators.updatePart({
      groupId: group.id,
      updates: { values: getRoomValuesUpdate(checked) },
    });
  };

  const handleToggleRoomValuesCategory = (categoryGroups: ScanTemplateGroup[], checked: boolean) => {
    const updatedTemplateRoot = categoryGroups.reduce((template, group) => {
      if (groupHasRoomValues(group) === checked) return template;
      return updateTemplateGroup(template, group.id, { values: getRoomValuesUpdate(checked) });
    }, templateRoot);
    if (updatedTemplateRoot === templateRoot) return;
    scanTemplateDataMutators.updateTemplate({ groupTemplates: updatedTemplateRoot.groupTemplates });
  };

  return (
    <div css={GROUP_MEASUREMENTS_EDITOR_STYLE}>
      {!readOnly && (
        <div css={ADD_FLOORING_TYPE_BAR_STYLE}>
          <LabeledInput
            label='New Flooring Type'
            value={newFlooringTypeName}
            placeholder='e.g. hardwood'
            maxLength={MAX_VALUE_NAME_LENGTH}
            onChange={setNewFlooringTypeName}
            onEnter={handleAddFlooringType}
          />
          <Button onClick={handleAddFlooringType} disabled={!present(newFlooringTypeName.trim())}>Add Flooring Type</Button>
        </div>
      )}

      <div css={TABLE_CONTAINER_STYLE}>
        <table css={TABLE_STYLE}>
          <thead>
            <tr>
              <th>Group</th>
              <th>Standard Measurements*</th>
              {flooringTypes.map(flooringType => (
                // full value name ("Hardwood Area") in headers; the short form ("Hardwood") is for pick-lists
                <th key={flooringType}>{upperCamelCase(flooringType, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clusteredGroups.map(({ category, groups }) => {
              const roomValuesCheckedCount = groups.filter(groupHasRoomValues).length;
              const allRoomValuesChecked = roomValuesCheckedCount === groups.length;
              return (
                <Fragment key={category}>
                  <tr>
                    <td css={CATEGORY_DIVIDER_CELL_STYLE}>
                      <span css={CATEGORY_DIVIDER_LABEL_STYLE}>{upperCamelCase(category, ' ')}</span>
                    </td>
                    <td css={CATEGORY_DIVIDER_CELL_STYLE}>
                      <Checkbox
                        checked={allRoomValuesChecked}
                        indeterminate={roomValuesCheckedCount > 0 && !allRoomValuesChecked}
                        disabled={readOnly}
                        title={`${allRoomValuesChecked ? 'Uncheck' : 'Check'} standard room measurements for all ${upperCamelCase(category, ' ')} groups`}
                        onChange={event => handleToggleRoomValuesCategory(groups, event.target.checked)}
                      />
                    </td>
                    {flooringTypes.map(flooringType => {
                      const checkedCount = groups.filter(group => groupHasFlooringType(group, flooringType)).length;
                      const allChecked = checkedCount === groups.length;
                      return (
                        <td key={flooringType} css={CATEGORY_DIVIDER_CELL_STYLE}>
                          <Checkbox
                            checked={allChecked}
                            indeterminate={checkedCount > 0 && !allChecked}
                            disabled={readOnly}
                            title={`${allChecked ? 'Uncheck' : 'Check'} ${flooringAreaValueNamePresenter(flooringType)} for all ${
                              upperCamelCase(category, ' ')
                            } groups`}
                            onChange={event => handleToggleCategory(groups, flooringType, event.target.checked)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                  {groups.map(group => (
                    <tr key={group.id}>
                      <td css={GROUP_NAME_CELL_STYLE}>
                        <span
                          css={onGroupClick && GROUP_NAME_LINK_STYLE}
                          onClick={onGroupClick ? () => onGroupClick(group.id) : undefined}
                        >
                          {group.name}
                        </span>
                      </td>
                      <td>
                        <Checkbox
                          checked={groupHasRoomValues(group)}
                          disabled={readOnly}
                          onChange={event => handleToggleRoomValuesCell(group, event.target.checked)}
                        />
                      </td>
                      {flooringTypes.map(flooringType => (
                        <td key={flooringType}>
                          <Checkbox
                            checked={groupHasFlooringType(group, flooringType)}
                            disabled={readOnly}
                            onChange={event => handleToggleCell(group, flooringType, event.target.checked)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div css={STANDARD_MEASUREMENTS_FOOTNOTE_STYLE}>
        * Standard measurements: width, length, height, area, perimeter, and wall area.
      </div>
    </div>
  );
};
